import { Grid, useMediaQuery, useTheme } from "@material-ui/core";
import { Block, Save } from "@material-ui/icons";
import { VALIDATE_MAXIMUM_EXPORT_CURRENT_QUANTITY } from "app/appConfig";
import CommonButton from "app/common/Component/Button/CommonButton";
import PopupForm from "app/common/Component/Popup/PopupForm";
import GlobitsAutocomplete from "app/common/form/GlobitsAutocomplete";
import GlobitsDateTimePicker from "app/common/form/GlobitsDateTimePicker";
import GlobitsNumberInput from "app/common/form/GlobitsNumberInput";
import GlobitsPagingAutocomplete from "app/common/form/GlobitsPagingAutocomplete";
import GlobitsTextField from "app/common/form/GlobitsTextField";
import useDefaultDepartment from "app/common/hooks/useDefaultDepartment";
import IconDelete from "app/common/Icon/IconDelete";
import { VALUE_TAB_CONSTRUCTION_MATERIALS_PRODUCT } from "app/common/LocalConst";
import { getDepartmentsUser, isAdmin } from "app/common/LocalFunction";
import { pagingDepartment } from "app/services/DepartmentService";
import { useStore } from "app/stores";
import { useFormikContext } from "formik";
import { observer } from "mobx-react";
import { memo } from "react";
import * as Yup from "yup";

const RequestStoreConstructionPopup = ({ patient }) => {
  const validationSchema = Yup.object().shape({
    datas: Yup.array()
      .min(1, "Cần thêm ít nhất 1 phiếu!")
      .of(
        Yup.object().shape({
          department: Yup.object().nullable().required("Chưa chọn cơ sở!"),
          items: Yup.array()
            .min(1, "Cần thêm ít nhất 1 đơn hàng!")
            .of(
              Yup.object().shape({
                exportedCurrentQuantity: Yup.number()
                  .typeError("Định dạng phải là số!")
                  .nullable()
                  .test(
                    "maximum-exported",
                    "Số lượng xuất hiện tại phải nhỏ hơn tồn",
                    function (value) {
                      if (!VALIDATE_MAXIMUM_EXPORT_CURRENT_QUANTITY)
                        return true;
                      const { inventoryQuantity } = this.parent;
                      if (value > (inventoryQuantity || 0)) return false;
                      return true;
                    }
                  )
                  .test(
                    "maximum-payment-exported",
                    "Số lượng xuất hiện tại không được vượt qua số lượng đã thanh toán",
                    function (value) {
                      if (!VALIDATE_MAXIMUM_EXPORT_CURRENT_QUANTITY)
                        return true;

                      const { soldQuantity, exportedQuantity, item } =
                        this.parent;
                      if (!item?.amountActuallyPaid) return true;
                      let validQuantityExport =
                        Math.floor(
                          (item?.totalBillPatientPaid /
                            item?.amountActuallyPaid) *
                            soldQuantity
                        ) - (exportedQuantity || 0);

                      if (value > validQuantityExport) return false;
                      return true;
                    }
                  ),
              })
            )
            .nullable(),
        })
      ),
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));

  const {
    initRequestStoreConstructionMaterial,
    handleConstructionMaterialFromHis,
    openRequestStoreConstructionMaterialPopup,
    handleCloseRequestStoreConstructionMaterialPopup,
  } = useStore().treatmentManagementStore.constructionMaterialsSynthesisStore;

  return (
    <PopupForm
      open={openRequestStoreConstructionMaterialPopup}
      size="lg"
      handleClose={handleCloseRequestStoreConstructionMaterialPopup}
      title={`Tạo phiếu xuất kho`}
      formik={{
        initialValues: {
          ...initRequestStoreConstructionMaterial,
        },
        onSubmit: handleConstructionMaterialFromHis,
        enableReinitialize: true,
        validationSchema: validationSchema,
      }}
      action={({ isSubmitting }) => {
        return (
          <div className="flex-center gap-1">
            <CommonButton
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {!isMobile && <Save className="mr-2 " />}
              {`Xác nhận`}
            </CommonButton>

            <CommonButton
              className="btn-gray"
              onClick={handleCloseRequestStoreConstructionMaterialPopup}
            >
              <Block className="mr-2" /> Đóng
            </CommonButton>
          </div>
        );
      }}
    >
      {({ setFieldValue, values }) => {
        return (
          <>
            <ValueForm />
          </>
        );
      }}
    </PopupForm>
  );
};

const ValueForm = observer(() => {
  const { setFieldValue, values } = useFormikContext();
  const department = useDefaultDepartment();

  const { handleInitSingleStoreRequest, currentTab } =
    useStore().treatmentManagementStore.constructionMaterialsSynthesisStore;
  const remove = (Pindex, index) => {
    setFieldValue(
      `datas[${Pindex}].items`,
      values.datas[Pindex].items.filter((_, i) => i !== index)
    );
  };
  const isAdminRole = isAdmin();
  const departmentsUser = getDepartmentsUser();
  const isDrug = currentTab == VALUE_TAB_CONSTRUCTION_MATERIALS_PRODUCT;
  return (
    <>
      {values?.datas?.map((data, Pindex) => (
        <div className="export-card" key={Pindex}>
          <div className="export-card-header">Phiếu xuất #{Pindex + 1}</div>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4} md={3}>
              <GlobitsPagingAutocomplete
                api={
                  isAdminRole
                    ? pagingDepartment
                    : async () => ({
                        data: { content: departmentsUser || [] },
                      })
                }
                name={`datas[${Pindex}].department`}
                label="Cơ sở"
                required
                onChange={async (_, value) => {
                  setFieldValue(`datas[${Pindex}].department`, value);
                  setFieldValue(
                    `datas[${Pindex}].store`,
                    value?.storeConfig || null
                  );
                  handleInitSingleStoreRequest(
                    value,
                    value?.storeConfig,
                    Pindex
                  );
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4} md={3}>
              <GlobitsAutocomplete
                options={
                  values?.datas?.[
                    Pindex
                  ]?.department?.departmentStoreConfigs?.map(
                    (el) => el?.storeConfig
                  ) || []
                }
                name={`datas[${Pindex}].store`}
                label="Kho yêu cầu"
                onChange={async (_, value) => {
                  setFieldValue(`datas[${Pindex}].store`, value);
                  handleInitSingleStoreRequest(
                    values?.datas?.[Pindex]?.department,
                    value,
                    Pindex
                  );
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4} md={3}>
              <GlobitsTextField
                name={`datas[${Pindex}].room.name`}
                label="Phòng"
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <GlobitsDateTimePicker
                name={`datas[${Pindex}].dateIssue`}
                label="Thời gian xuất"
                maxDate={new Date()}
                maxDateMessage="Thời gian xuất phải trước hiện tại"
                required
                isDateTimePicker
              />
            </Grid>

            {/* =================== TABLE =================== */}
            <Grid item xs={12}>
              <div className="export-table-wrapper">
                <table className="export-table">
                  <thead>
                    <tr>
                      <th width="5%">STT</th>
                      <th width="20%">Tên {isDrug ? "sản phẩm" : "vật tư"}</th>
                      <th width="12%">Số lượng thực tế yêu cầu</th>
                      <th width="12%">Số lượng xuất</th>
                      <th width="13%">Đơn vị tính</th>
                      <th width="13%">Tồn</th>
                      <th width="10%">Thao tác</th>
                    </tr>
                  </thead>

                  <tbody>
                    {data?.items?.map((item, index) => (
                      <tr key={`${Pindex}-${index}`}>
                        <td align="center">{index + 1}</td>

                        <td>
                          {isDrug ? (
                            <>
                              {item?.drug?.name} - {item?.drug?.code}
                            </>
                          ) : (
                            <>
                              {item?.medicalSupply?.name} -{" "}
                              {item?.medicalSupply?.code}
                            </>
                          )}
                        </td>

                        {/* Số lượng yêu cầu */}
                        <td>
                          <GlobitsNumberInput
                            name={`datas[${Pindex}].items[${index}].actualQuantity`}
                            disabled
                          />
                        </td>

                        {/* Số lượng xuất */}
                        <td>
                          <GlobitsNumberInput
                            name={`datas[${Pindex}].items[${index}].exportedCurrentQuantity`}
                            disabled
                          />
                        </td>

                        {/* Đơn vị tính */}
                        <td>
                          <GlobitsTextField
                            name={`datas[${Pindex}].items[${index}].stockUnitName`}
                            disabled
                          />
                        </td>

                        {/* Tồn */}
                        <td>
                          <GlobitsNumberInput
                            name={`datas[${Pindex}].items[${index}].inventoryQuantity`}
                            decimal
                            roundNumberToFixed={4}
                            disabled
                          />
                        </td>

                        {/* Xóa */}
                        <td align="center">
                          <span
                            className="pointer delete-btn"
                            onClick={() => remove(Pindex, index)}
                          >
                            <IconDelete />
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Grid>
          </Grid>
        </div>
      ))}
    </>
  );
});

export default memo(observer(RequestStoreConstructionPopup));
